/**
 * App Settings Hook - Database-backed user settings
 *
 * Stores user-specific app settings in the database via SettingsService.
 * Settings are loaded on first access and cached in memory.
 */

import { useEffect } from 'react';
import { create } from 'zustand';

import { SettingsService } from '~/services/settings-service';
import type { AppSettings, BondInterestRateSettings } from '~/types/app-settings';
import { DEFAULT_APP_SETTINGS } from '~/types/app-settings';

interface AppSettingsStore {
  /** Current app settings */
  settings: AppSettings;

  /** Loading state */
  isLoading: boolean;

  /** Whether settings have been loaded from the database */
  hasLoaded: boolean;

  /** Error state */
  error: string | null;

  /** Load settings from database */
  loadSettings: () => Promise<void>;

  /** Update bond interest rate settings (saves to database) */
  updateBondInterestRates: (rates: BondInterestRateSettings) => Promise<void>;

  /** Reset to defaults (saves to database) */
  resetToDefaults: () => Promise<void>;
}

export const useAppSettings = create<AppSettingsStore>()((set, get) => ({
  settings: DEFAULT_APP_SETTINGS,
  isLoading: false,
  hasLoaded: false,
  error: null,

  loadSettings: async () => {
    // Prevent duplicate loads
    if (get().isLoading || get().hasLoaded) {
      return;
    }

    set({ isLoading: true, error: null });

    try {
      const bondInterestRates = await SettingsService.getOrCreateBondInterestRateDefaults();

      set({
        settings: {
          bondInterestRates,
        },
        isLoading: false,
        hasLoaded: true,
      });
    } catch (error) {
      console.error('[useAppSettings] Failed to load settings:', error);
      set({
        isLoading: false,
        hasLoaded: true,
        error: 'Failed to load settings',
      });
    }
  },

  updateBondInterestRates: async (rates) => {
    // Optimistic update
    set((state) => ({
      settings: {
        ...state.settings,
        bondInterestRates: rates,
      },
    }));

    // Save to database
    const result = await SettingsService.setBondInterestRateDefaults(rates);

    if (!result) {
      console.error('[useAppSettings] Failed to save bond interest rate settings');
      // Could revert optimistic update here if needed
    }
  },

  resetToDefaults: async () => {
    // Optimistic update
    set({ settings: DEFAULT_APP_SETTINGS });

    // Save defaults to database
    const result = await SettingsService.setBondInterestRateDefaults(
      DEFAULT_APP_SETTINGS.bondInterestRates
    );

    if (!result) {
      console.error('[useAppSettings] Failed to reset settings to defaults');
    }
  },
}));

/**
 * Hook to ensure settings are loaded on mount
 * Use this in components that need settings
 */
export const useAppSettingsLoader = () => {
  const { loadSettings, hasLoaded, isLoading } = useAppSettings();

  useEffect(() => {
    if (!hasLoaded && !isLoading) {
      loadSettings();
    }
  }, [hasLoaded, isLoading, loadSettings]);

  return useAppSettings();
};
